import React, { useState, useRef, useEffect } from 'react';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import api from '../utils/api';

/**
 * Textarea that supports @mention typeahead.
 *
 * Props:
 *  - value, onChange(text)
 *  - mentions, onMentionsChange(array of {id, name})
 *  - placeholder, className, testId
 */
const MentionInput = ({
  value,
  onChange,
  mentions = [],
  onMentionsChange,
  placeholder,
  className = '',
  testId,
  minHeight = '100px',
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [activeQuery, setActiveQuery] = useState(null); // { start, query } or null
  const textareaRef = useRef(null);

  const getInitials = (name) =>
    name?.split(' ').map((n) => n[0]).join('').toUpperCase() || 'U';

  // Detect "@xxxx" right before caret
  const detectMentionQuery = (text, caret) => {
    const before = text.slice(0, caret);
    const match = before.match(/(?:^|\s)@([\w\s]{0,30})$/);
    if (!match) return null;
    const query = match[1];
    const start = before.lastIndexOf('@');
    return { start, query };
  };

  const handleChange = (e) => {
    const text = e.target.value;
    const caret = e.target.selectionStart;
    onChange(text);

    // Drop any mention whose @name no longer appears in the text
    const stillPresent = mentions.filter((m) => text.includes(`@${m.name}`));
    if (stillPresent.length !== mentions.length) {
      onMentionsChange(stillPresent);
    }

    const q = detectMentionQuery(text, caret);
    setActiveQuery(q);
  };

  // Fetch mentionable users when query changes
  useEffect(() => {
    if (activeQuery === null) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const fetch = async () => {
      try {
        const { data } = await api.get('/users/mentionable', {
          params: activeQuery.query ? { q: activeQuery.query } : {},
        });
        if (!cancelled) setSuggestions(data);
      } catch (err) {
        if (!cancelled) setSuggestions([]);
      }
    };
    fetch();
    return () => {
      cancelled = true;
    };
  }, [activeQuery?.query, activeQuery === null]);

  const pickSuggestion = (u) => {
    if (!activeQuery) return;
    const before = value.slice(0, activeQuery.start);
    const afterCaret = value.slice(textareaRef.current.selectionStart);
    const insertion = `@${u.name} `;
    const newText = `${before}${insertion}${afterCaret}`;
    onChange(newText);

    // Add mention if not already present
    if (!mentions.some((m) => m.id === u.id && m.name === u.name)) {
      onMentionsChange([...mentions, { id: u.id, name: u.name }]);
    }

    setActiveQuery(null);
    // Restore caret right after the inserted mention
    setTimeout(() => {
      const pos = before.length + insertion.length;
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(pos, pos);
    }, 0);
  };

  const showDropdown = activeQuery !== null && suggestions.length > 0;

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        className={`resize-none ${className}`}
        style={{ minHeight }}
        data-testid={testId}
      />
      {showDropdown && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
          data-testid="mention-dropdown"
        >
          {suggestions.map((u) => (
            <button
              key={u.id}
              type="button"
              onMouseDown={(e) => {
                // mousedown to fire before textarea blur
                e.preventDefault();
                pickSuggestion(u);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50"
              data-testid={`mention-suggestion-${u.id}`}
            >
              <Avatar className="h-7 w-7">
                <AvatarImage src={u.profile_photo} />
                <AvatarFallback className="bg-[#1E3A8A] text-white text-xs">
                  {getInitials(u.name)}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm text-slate-800">{u.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MentionInput;
